// src/pages/VehicleV2/BatteryCardListing.jsx

import { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaEye,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/batteryCardListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const BatteryCardListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8
  );

  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("financialyear") ||
    sessionStorage.getItem("finyear") ||
    "";

  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("financialyearid") ||
    sessionStorage.getItem("finyearid") ||
    "";

  const requestConfig = useMemo(() => {
    const headers = {
      "x-branchid": branchId,
      "x-userid": loginUserId,
    };
    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }
    return { headers };
  }, [finYear, finYearId, branchId, loginUserId]);

  const [ddPurchaseFrom, setDdPurchaseFrom] = useState([]);
  const [ddBatteryTypes, setDdBatteryTypes] = useState([]);
  const [ddBatteryMakes, setDdBatteryMakes] = useState([]);

  const [batteryType, setBatteryType] = useState("");
  const [batteryMake, setBatteryMake] = useState("");
  const [batteryCode, setBatteryCode] = useState("");
  const [batterySerialNo, setBatterySerialNo] = useState("");
  const [purchaseFrom, setPurchaseFrom] = useState("");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const loadDropdowns = useCallback(async () => {
    try {
      if (!branchId) return;
      const { data } = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId },
        ...requestConfig,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load dropdowns");
      setDdPurchaseFrom(data.purchaseFrom || []);
      setDdBatteryTypes(data.batteryTypes || []);
      setDdBatteryMakes(data.batteryMakes || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Dropdowns error");
    }
  }, [branchId, requestConfig]);

  const loadList = useCallback(
    async (p = 1) => {
      try {
        if (!branchId) {
          toast.warn("branchId not found in localStorage");
          return;
        }

        setLoading(true);
        setHasSearched(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchId,
            page: p,
            limit,
            batteryType,
            batteryMake,
            batteryCode,
            serialNo: batterySerialNo,
            purchaseFrom,
            search,
          },
          ...requestConfig,
        });

        if (!data?.success) throw new Error(data?.message || "Failed to load list");

        setRows(data.rows || []);
        setTotal(toInt(data.total, 0));
        setTotalPages(Math.max(1, toInt(data.totalPages, 1)));
        setPage(toInt(data.page, p));
        setSelectedIds([]);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "List loading error");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [
      branchId,
      limit,
      batteryType,
      batteryMake,
      batteryCode,
      batterySerialNo,
      purchaseFrom,
      search,
      requestConfig,
    ]
  );

  useEffect(() => {
    if (!branchId) return;
    loadDropdowns();
    loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(rows.map((r) => r.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleClearFilters = () => {
    setBatteryType("");
    setBatteryMake("");
    setBatteryCode("");
    setBatterySerialNo("");
    setPurchaseFrom("");
    setSearch("");
    toast.info("Filters cleared");
  };

  const handleSearchClick = () => {
    setPage(1);
    loadList(1);
  };

  const handleDelete = async () => {
    try {
      if (!selectedIds.length) {
        toast.warn("Please select at least one battery to delete!");
        return;
      }
      if (!branchId) {
        toast.warn("branchId missing");
        return;
      }

      const ok = window.confirm(`Delete ${selectedIds.length} battery card(s)?`);
      if (!ok) return;

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        { branchId, loginUserId, ids: selectedIds },
        requestConfig
      );

      if (!data?.success) throw new Error(data?.message || "Delete failed");

      if (data.blockedCount > 0) {
        toast.warn(`Deleted: ${data.deletedCount}, Blocked (in use): ${data.blockedCount}`);
      } else {
        toast.success("Deleted successfully");
      }

      const nextPage =
        rows.length === selectedIds.length && page > 1 ? page - 1 : page;
      loadList(nextPage);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    }
  };

  const handleEdit = (id) => {
    sessionStorage.setItem("BatteryID", String(id));
    navigate(`/vehicle/BatteryCardEntry?batteryId=${id}`);
  };

  const handleView = (id) => {
    sessionStorage.setItem("BatteryID", String(id));
    navigate(`/vehicle/BatteryCardEntry?view=1&batteryId=${id}`);
  };

  const handleAddNew = () => {
    sessionStorage.removeItem("BatteryID");
    navigate("/vehicle/BatteryCardEntry");
  };

  const pageNumbers = useMemo(() => {
    const tp = totalPages;
    const p = page;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    const nums = new Set([1, tp, p, p - 1, p + 1]);
    const arr = [...nums].filter((n) => n >= 1 && n <= tp).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      out.push(arr[i]);
      if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push("...");
    }
    return out;
  }, [page, totalPages]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Battery Cards
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleSearchClick}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className={labelClass}>Search Battery Cards</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                    placeholder="Type to search battery cards..."
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Battery Type</label>
                  <select
                    value={batteryType}
                    onChange={(e) => setBatteryType(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {ddBatteryTypes.map((x) => (
                      <option key={x.id} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Serial No</label>
                  <input
                    type="text"
                    value={batterySerialNo}
                    onChange={(e) => setBatterySerialNo(e.target.value)}
                    className={inputClass}
                    placeholder="Enter serial number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Battery Code</label>
                  <input
                    type="text"
                    value={batteryCode}
                    onChange={(e) => setBatteryCode(e.target.value)}
                    className={inputClass}
                    placeholder="Enter battery code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Battery Make</label>
                  <select
                    value={batteryMake}
                    onChange={(e) => setBatteryMake(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {ddBatteryMakes.map((x) => (
                      <option key={x.id} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Purchase From</label>
                  <select
                    value={purchaseFrom}
                    onChange={(e) => setPurchaseFrom(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {ddPurchaseFrom.map((x) => (
                      <option key={x.id} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Battery Cards Found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Battery Code</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Reg No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Battery Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Battery Make</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Serial Number</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Expected Life</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Purchased From</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={handleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.batteryCode}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.vehicleRegNo}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.batteryType}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.batteryMake}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.serialNumber}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.expectedLife}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.purchasedFrom}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleEdit(r.id)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => handleView(r.id)}
                              className="text-blue-500 hover:text-blue-600"
                              title="View"
                            >
                              <FaEye />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="p-4">
                  <button
                    onClick={handleDelete}
                    disabled={selectedIds.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                      selectedIds.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <FaTrash />
                    Remove
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing <b>{total === 0 ? 0 : (page - 1) * limit + 1}</b> to{" "}
              <b>{Math.min(page * limit, total)}</b> of <b>{total}</b> results
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => {
                  const np = Math.max(page - 1, 1);
                  setPage(np);
                  loadList(np);
                }}
                disabled={page === 1}
                className={`px-3 py-2 border rounded-lg ${
                  page === 1 ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Prev
              </button>

              {pageNumbers.map((pg, i) =>
                pg === "..." ? (
                  <span key={`d-${i}`} className="px-2">
                    ...
                  </span>
                ) : (
                  <button
                    key={pg}
                    onClick={() => {
                      setPage(pg);
                      loadList(pg);
                    }}
                    className={`px-3 py-2 border rounded-lg ${
                      page === pg
                        ? `${buttonClass} text-white border-transparent`
                        : isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pg}
                  </button>
                )
              )}

              <button
                onClick={() => {
                  const np = Math.min(page + 1, totalPages);
                  setPage(np);
                  loadList(np);
                }}
                disabled={page === totalPages}
                className={`px-3 py-2 border rounded-lg ${
                  page === totalPages ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardListing;